import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  Building2,
  Check,
  Clock,
  ImagePlus,
  LogOut,
  Mail,
  ShieldCheck,
  Trash2,
  User as UserIcon,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { Card, CardBody } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Field, Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { Spinner } from "../components/ui/Spinner";
import { useAuth } from "../lib/auth";
import * as api from "../lib/api";
import { ApiError, ROLES } from "../lib/api";

function initials(name: string | null, email: string): string {
  const src = (name && name.trim()) || email;
  const p = src.split(/[\s@.]+/).filter(Boolean);
  return (p[0]?.[0] ?? "?").toUpperCase() + (p[1]?.[0] ?? "").toUpperCase();
}

const selectClass =
  "rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 focus-ring";

export default function Settings() {
  const { user, currentTenant, logout, refresh } = useAuth();
  const tenant = currentTenant?.slug;
  const isAdmin = currentTenant?.role === "owner" || currentTenant?.role === "admin";

  const [data, setData] = useState<api.MembersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("builder");
  const [notice, setNotice] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!tenant) return;
    setLoading(true);
    setError(null);
    try {
      setData(await api.listMembers(tenant));
      setLogoUrl(await api.logoObjectUrl(tenant));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't load workspace settings");
    } finally {
      setLoading(false);
    }
  }, [tenant]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(fn: () => Promise<unknown>, ok?: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (ok) setNotice(ok);
      await load();
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  async function onLogo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !tenant) return;
    await act(() => api.uploadLogo(tenant, file), "Logo updated");
    if (fileRef.current) fileRef.current.value = "";
  }

  return (
    <>
      <PageHeader
        title="Settings"
        description="Your account, workspace branding, and who can access this workspace."
      />

      {notice && (
        <div className="mb-6 flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-700 ring-1 ring-inset ring-emerald-100">
          <Check className="h-4 w-4" /> {notice}
        </div>
      )}
      {error && (
        <div className="mb-6 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 ring-1 ring-inset ring-rose-100">
          {error}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Account */}
        <Card>
          <CardBody>
            <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <UserIcon className="h-4 w-4 text-brand-600" /> Your account
            </h2>
            <div className="mt-4 flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-gradient text-sm font-bold text-white">
                {user ? initials(user.full_name, user.email) : "?"}
              </span>
              <div className="min-w-0">
                <p className="truncate font-semibold text-slate-900">{user?.full_name ?? "—"}</p>
                <p className="flex items-center gap-1.5 truncate text-sm text-slate-500">
                  <Mail className="h-3.5 w-3.5" /> {user?.email}
                </p>
              </div>
            </div>
            <p className="mt-4 flex items-start gap-2 rounded-xl bg-slate-50 px-3.5 py-2.5 text-xs text-slate-500">
              <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
              Identity, password and MFA are managed in Microsoft Entra.
            </p>
            <Button variant="outline" className="mt-4" onClick={logout}>
              <LogOut className="h-4 w-4" /> Sign out
            </Button>
          </CardBody>
        </Card>

        {/* Workspace + logo */}
        <Card>
          <CardBody>
            <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Building2 className="h-4 w-4 text-brand-600" /> Workspace
            </h2>
            <div className="mt-4 flex items-center gap-4">
              <span className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                {logoUrl ? (
                  <img src={logoUrl} alt="Workspace logo" className="h-full w-full object-contain" />
                ) : (
                  <Building2 className="h-6 w-6 text-slate-300" />
                )}
              </span>
              <div className="min-w-0">
                <p className="truncate font-semibold text-slate-900">{currentTenant?.display_name}</p>
                <p className="truncate font-mono text-xs text-slate-400">{tenant}</p>
                <Badge tone="brand" className="mt-1.5">{currentTenant?.role}</Badge>
              </div>
            </div>
            {isAdmin ? (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  className="hidden"
                  onChange={onLogo}
                />
                <Button variant="outline" size="sm" loading={busy} onClick={() => fileRef.current?.click()}>
                  <ImagePlus className="h-4 w-4" /> {logoUrl ? "Replace logo" : "Upload logo"}
                </Button>
                {logoUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => act(() => api.deleteLogo(tenant!).then(() => setLogoUrl(null)), "Logo removed")}
                  >
                    <Trash2 className="h-4 w-4 text-rose-500" /> Remove
                  </Button>
                )}
                <span className="text-xs text-slate-400">PNG, JPEG, SVG or WebP · max 2 MB</span>
              </div>
            ) : (
              <p className="mt-4 text-xs text-slate-400">Only owners and admins can change branding.</p>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Members */}
      <Card className="mt-6">
        <CardBody>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Users className="h-4 w-4 text-brand-600" /> People
            </h2>
            {data && (
              <span className="text-xs text-slate-400">
                {data.members.length} member{data.members.length === 1 ? "" : "s"}
                {data.requests.length > 0 && ` · ${data.requests.length} pending`}
              </span>
            )}
          </div>

          {isAdmin && (
            <form
              className="mt-5 flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4"
              onSubmit={(e) => {
                e.preventDefault();
                void act(
                  () => api.addMember(tenant!, inviteEmail, inviteRole).then(() => setInviteEmail("")),
                  "Invitation sent",
                );
              }}
            >
              <div className="min-w-[220px] flex-1">
                <Field label="Add someone by email">
                  <Input
                    required
                    type="email"
                    placeholder="teammate@company.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                </Field>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Role</label>
                <select
                  className="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-700 focus-ring"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                >
                  {ROLES.filter((r) => r !== "owner").map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <Button type="submit" loading={busy}>
                <UserPlus className="h-4 w-4" /> Add
              </Button>
            </form>
          )}

          {loading ? (
            <div className="flex justify-center py-10">
              <Spinner className="h-6 w-6 text-brand-500" />
            </div>
          ) : !data ? null : (
            <div className="mt-5 space-y-6">
              {/* Pending access requests */}
              {data.requests.length > 0 && (
                <div>
                  <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-amber-600">
                    <Clock className="h-3.5 w-3.5" /> Access requests
                  </p>
                  <ul className="mt-2 space-y-2">
                    {data.requests.map((m) => (
                      <li key={m.user_id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-800">{m.full_name ?? m.email}</p>
                          <p className="truncate text-xs text-slate-500">{m.email}</p>
                        </div>
                        {isAdmin && (
                          <div className="flex items-center gap-2">
                            <select
                              className={selectClass}
                              defaultValue="builder"
                              onChange={(e) => void act(() => api.approveMember(tenant!, m.user_id, e.target.value), "Access granted")}
                            >
                              <option value="" disabled>Approve as…</option>
                              {ROLES.filter((r) => r !== "owner").map((r) => (
                                <option key={r} value={r}>{r}</option>
                              ))}
                            </select>
                            <Button size="sm" loading={busy} onClick={() => void act(() => api.approveMember(tenant!, m.user_id, "builder"), "Access granted")}>
                              <Check className="h-4 w-4" /> Approve
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => void act(() => api.removeMember(tenant!, m.user_id), "Request declined")}>
                              <X className="h-4 w-4 text-rose-500" />
                            </Button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Members */}
              <ul className="space-y-2">
                {data.members.map((m) => (
                  <li key={m.user_id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-100 px-4 py-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-600">
                        {initials(m.full_name, m.email)}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-800">
                          {m.full_name ?? m.email}
                          {m.user_id === user?.id && <span className="ml-2 text-xs text-slate-400">(you)</span>}
                        </p>
                        <p className="truncate text-xs text-slate-500">{m.email}</p>
                      </div>
                    </div>
                    {isAdmin && m.user_id !== user?.id ? (
                      <div className="flex items-center gap-2">
                        <select
                          className={selectClass}
                          value={m.role}
                          onChange={(e) => void act(() => api.changeMemberRole(tenant!, m.user_id, e.target.value), "Role updated")}
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                        <Button size="sm" variant="ghost" onClick={() => void act(() => api.removeMember(tenant!, m.user_id), "Member removed")}>
                          <Trash2 className="h-4 w-4 text-rose-500" />
                        </Button>
                      </div>
                    ) : (
                      <Badge tone="slate">{m.role}</Badge>
                    )}
                  </li>
                ))}
              </ul>

              {/* Invited, not yet signed in */}
              {data.invites.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Invited — will join on first sign-in
                  </p>
                  <ul className="mt-2 space-y-2">
                    {data.invites.map((i) => (
                      <li key={i.id} className="flex items-center justify-between rounded-xl border border-dashed border-slate-200 px-4 py-2.5">
                        <span className="truncate text-sm text-slate-600">{i.email}</span>
                        <span className="flex items-center gap-2">
                          <Badge tone="slate">{i.role}</Badge>
                          {isAdmin && (
                            <Button size="sm" variant="ghost" onClick={() => void act(() => api.revokeInvite(tenant!, i.id), "Invitation revoked")}>
                              <X className="h-4 w-4 text-slate-400" />
                            </Button>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Danger zone — owners only */}
      {currentTenant?.role === "owner" && (
        <Card className="mt-6 border-rose-200">
          <CardBody>
            <h2 className="flex items-center gap-2 text-sm font-semibold text-rose-700">
              <AlertTriangle className="h-4 w-4" /> Danger zone
            </h2>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-rose-200 bg-rose-50/50 px-5 py-4">
              <div>
                <p className="text-sm font-semibold text-slate-900">Delete this workspace</p>
                <p className="mt-0.5 text-sm text-slate-600">
                  Permanently removes <span className="font-semibold">{currentTenant.display_name}</span>,
                  its members, connections, workflows and stored configuration. This cannot be undone.
                </p>
              </div>
              <Button variant="danger" onClick={() => setDeleteOpen(true)}>
                <Trash2 className="h-4 w-4" /> Delete workspace
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete workspace?">
        <div className="space-y-4 px-6 py-5">
          <div className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-800 ring-1 ring-inset ring-rose-100">
            This permanently deletes <span className="font-semibold">{currentTenant?.display_name}</span> —
            every member, connection, workflow, exchange listing and stored secret. It cannot be undone.
          </div>
          <Field
            label={`Type "${tenant}" to confirm`}
            hint="This must match the workspace name exactly."
          >
            <Input
              autoFocus
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={tenant}
            />
          </Field>
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-6 py-4">
          <Button variant="ghost" onClick={() => setDeleteOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={deleting}
            disabled={confirmText !== tenant}
            onClick={async () => {
              if (!tenant) return;
              setDeleting(true);
              setError(null);
              try {
                await api.deleteWorkspace(tenant, confirmText);
                localStorage.removeItem("wise.tenant");
                await refresh();
                navigate("/app", { replace: true });
              } catch (e) {
                setError(e instanceof ApiError ? e.message : "Couldn't delete the workspace");
                setDeleting(false);
                setDeleteOpen(false);
              }
            }}
          >
            Delete permanently
          </Button>
        </div>
      </Modal>
    </>
  );
}
